import AttendanceCoverageReport from "@/services/api/attendanceCoverageReport";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface AttendanceCoverageReportDetails {
    attendanceCoverageReportDetails: any[];
    attendanceSummary: any;
    attendeanceGeofenceReport: any[];
    loading: {
        attendanceCoverageReportDetails: boolean,
        attendanceSummary: boolean;
        attendanceExport: boolean;
        attendanceSummaryExport: boolean;
    };
    error: string | null;
}

const initialState: AttendanceCoverageReportDetails = {
    attendanceCoverageReportDetails: [],
    attendanceSummary: null,
    attendeanceGeofenceReport: [],
    loading: {
        attendanceCoverageReportDetails: false,
        attendanceSummary: false,
        attendanceExport: false,
        attendanceSummaryExport: false,
    },
    error: null,
}

export const getAttendanceCoverageReportDetails = createAsyncThunk(
    "report/getAttendanceCoverageReportDetails",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await AttendanceCoverageReport.getAttendanceCoverageDetails(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch attendance");
        }
    }
);

export const getAttendanceCoverageExport = createAsyncThunk(
    "report/getAttendanceCoverageExport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await AttendanceCoverageReport.getAttendanceCoverageExport(payload);
            return saveAsExcelFile(res, "ATTENDANCE_REPORT");
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to export");
        }
    }
);

export const getAttendanceSummary = createAsyncThunk(
    "report/getAttendanceSummary",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await AttendanceCoverageReport.getAttendanceSummary(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch attendance");
        }
    }
);

export const getAttendanceSummaryExport = createAsyncThunk(
    "report/getAttendanceSummaryExport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await AttendanceCoverageReport.getAttendanceSummaryExport(payload);
            return saveAsExcelFile(res, "ATTENDANCE_SUMMARY_REPORT");
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to export");
        }
    }
);

const attendanceCoverageReport = createSlice({
    name: "attendanceCoverageReport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(getAttendanceCoverageReportDetails.pending, (state) => {
            state.error = null;
            state.attendanceCoverageReportDetails = [];
            state.loading.attendanceCoverageReportDetails = true;
        })
        .addCase(getAttendanceCoverageReportDetails.fulfilled, (state, action) => {
            state.error = null;
            state.attendanceCoverageReportDetails = action.payload || [];
            state.loading.attendanceCoverageReportDetails = false;
        })
        .addCase(getAttendanceCoverageReportDetails.rejected, (state, action) => {
            state.error = action.payload as string;
            state.attendanceCoverageReportDetails = [];
            state.loading.attendanceCoverageReportDetails = false;
        })

        .addCase(getAttendanceCoverageExport.pending, (state) => {
            state.error = null;
            state.loading.attendanceExport = true;
        })
        .addCase(getAttendanceCoverageExport.fulfilled, (state) => {
            state.error = null;
            state.loading.attendanceExport = false;
        })
        .addCase(getAttendanceCoverageExport.rejected, (state, action) => {
            state.error = action.payload as string;
            state.loading.attendanceExport = false;
        })

        .addCase(getAttendanceSummary.pending, (state) => {
            state.error = null;
            state.attendanceSummary = null;
            state.loading.attendanceSummary = true;
        })
        .addCase(getAttendanceSummary.fulfilled, (state, action) => {
            state.error = null;
            state.attendanceSummary = action.payload || null;
            state.loading.attendanceSummary = false;
        })
        .addCase(getAttendanceSummary.rejected, (state, action) => {
            state.error = action.payload as string;
            state.attendanceSummary = null;
            state.loading.attendanceSummary = false;
        })

        .addCase(getAttendanceSummaryExport.pending, (state) => {
            state.error = null;
            state.loading.attendanceSummaryExport = true;
        })
        .addCase(getAttendanceSummaryExport.fulfilled, (state, action) => {
            state.error = null;
            state.loading.attendanceSummaryExport = false;
        })
        .addCase(getAttendanceSummaryExport.rejected, (state, action) => {
            state.error = action.payload as string;
            state.loading.attendanceSummaryExport = false;
        });

    },
});

export default attendanceCoverageReport.reducer;